
import { createRoot } from 'react-dom/client'
import React, { lazy, Suspense } from 'react'
import './index.css'

// Create a minimal loading component
const AppLoading = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-shipping"></div>
  </div>
)

// Create a fallback error component with more detailed error information
const AppError = ({ error }: { error?: Error }) => {
  const errorMessage = error?.message || "Unknown error loading the application";
  const isConfigError = errorMessage.includes('Airtable API key') || 
                        errorMessage.includes('Airtable Base ID') ||
                        errorMessage.includes('configuration missing');
  
  return (
    <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
      <h2 className="text-2xl font-bold text-amber-600 mb-4">Application Notice</h2>
      <p className="mb-2">{errorMessage}</p>
      
      {isConfigError && (
        <>
          <p className="mb-4">The application will use mock data for demonstration purposes.</p>
          <div className="bg-gray-100 p-4 rounded text-left mb-4">
            <p className="font-medium mb-2">To use real data, set up these environment variables:</p>
            <ul className="list-disc list-inside">
              <li>VITE_AIRTABLE_API_KEY</li>
              <li>VITE_AIRTABLE_BASE_ID</li>
            </ul>
          </div>
        </>
      )}
      
      <button 
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={() => window.location.reload()}
      >
        Continue with Mock Data
      </button>
    </div>
  );
};

// Use a more robust dynamic import pattern with error handling
const App = lazy(() => import('./App.tsx')
  .catch(error => {
    console.error('Error loading App component:', error);
    // Return a module with a default export that renders the error state
    return { 
      default: () => <AppError error={error} /> 
    };
  })
);

// Mount the app with comprehensive error handling
const mountApp = () => {
  try {
    const rootElement = document.getElementById('root')
    if (!rootElement) throw new Error('Root element not found')
    
    const root = createRoot(rootElement)
    
    // Check for environment variables early but don't block app rendering
    const apiKey = import.meta.env.VITE_AIRTABLE_API_KEY;
    const baseId = import.meta.env.VITE_AIRTABLE_BASE_ID;
    
    if (!apiKey || !baseId) {
      console.log('Missing Airtable configuration - will use mock data');
      // We'll still mount the app, it will use mock data
    }
    
    // Add global error handler for lazy loading
    window.addEventListener('error', (event) => {
      console.error('Global error:', event);
      // Don't stop the error from propagating
    });
    
    root.render(
      <React.StrictMode>
        <Suspense fallback={<AppLoading />}>
          <App />
        </Suspense>
      </React.StrictMode>
    )
  } catch (error) {
    console.error('App mounting error:', error);
    
    // Attempt recovery by rendering error state directly
    const rootElement = document.getElementById('root')
    if (rootElement) {
      const root = createRoot(rootElement)
      root.render(<AppError error={error instanceof Error ? error : new Error('Unknown error')} />)
    }
  }
}

// Start the app
mountApp()
